import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';
import quotationService from '../../services/quotationService';
import { getCarInventory, upsertCarToInventory, fetchCarInventoryFromServer } from '../../utils/carInventoryStorage';
import { getBackendBaseUrl } from '../../utils/imageUrlResolver';
import { useAppDispatch } from '../../redux/hooks';
import { globalForceRefreshCarsData } from '../../redux/slices/globalCarsSlice';

interface InventoryStructure {
  brands: string[];
  modelsByBrand: Record<string, string[]>;
  variantsByModel: Record<string, string[]>;
  imagesByModel: Record<string, string>;
  typesByModel: Record<string, 't-permit' | 'private'>;
}

// Helper to build structured brand, model and variant map from storage
const getInventoryStructure = (): InventoryStructure => {
  const inventory = getCarInventory();
  const knownBrands = ['Maruti Suzuki', 'Hyundai', 'Toyota', 'Tata', 'Mahindra', 'Kia', 'Honda'];
  const brandsSet = new Set<string>(knownBrands);
  const modelsByBrand: Record<string, string[]> = {};
  const variantsByModel: Record<string, string[]> = {};
  const imagesByModel: Record<string, string> = {};
  const typesByModel: Record<string, 't-permit' | 'private'> = {};

  knownBrands.forEach(b => {
    modelsByBrand[b] = [];
  });

  inventory.forEach(car => {
    let matchedBrand = knownBrands.find(b => car.name.toLowerCase().startsWith(b.toLowerCase()));
    let modelName = car.name;
    if (matchedBrand) {
      modelName = car.name.slice(matchedBrand.length).trim();
    } else {
      const parts = car.name.split(' ');
      matchedBrand = parts[0] || 'Other';
      modelName = parts.slice(1).join(' ') || car.name;
      brandsSet.add(matchedBrand);
    }

    if (!modelsByBrand[matchedBrand]) modelsByBrand[matchedBrand] = [];
    if (!modelsByBrand[matchedBrand].includes(modelName)) {
      modelsByBrand[matchedBrand].push(modelName);
    }

    const key = `${matchedBrand} ${modelName}`.trim();
    const varNames = (car.variants || []).map(v => v.name);
    variantsByModel[key] = varNames;
    variantsByModel[modelName] = varNames;

    if (car.image) {
      imagesByModel[key] = car.image;
      imagesByModel[modelName] = car.image;
    }
    if (car.carType) {
      typesByModel[key] = car.carType;
      typesByModel[modelName] = car.carType;
    }
  });

  return {
    brands: Array.from(brandsSet),
    modelsByBrand,
    variantsByModel,
    imagesByModel,
    typesByModel
  };
};

// Helper to build dynamic carVariants map from storage
const getDynamicCarVariants = (): Record<string, string[]> => {
  const inventory = getCarInventory();
  const map: Record<string, string[]> = {};
  inventory.forEach(car => {
    map[car.name] = (car.variants || []).map(v => v.name);
  });
  return map;
};

// Interface for quotation data
interface QuotationData {
  emiInterestRate: string;
  showroomCost: string;
  tcs: string;
  registration: string;
  insurance: string;
  noPlate: string;
  gps: string;
  fastag: string;
  speedGovernor: string;
  accessories: string;
  onRoadPrice: string;
  loanAmount: string;
  margin: string;
  processFee: string;
  stampDuty: string;
  handlingCharge: string;
  loanInsurance: string;
  // Bank percentage fields
  sbiBank: string;
  unionBank: string;
  indusIndBank: string;
  auBank: string;
  // Additional financial fields
  downPayment: string;
  offers: string;
  finalDownPayment: string;
  emiYears: string;
  emiFor5Years: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  [key: string]: string; // Index signature for dynamic access
}

// Interface for form errors
interface FormErrors {
  [key: string]: string;
}

// Interface for quotation response from API
interface QuotationResponse {
  vendor_id: string;
  car_model: string;
  model_variant: string;
  roi_emi_interest: number;
  sbi_bank: number;
  union_bank: number;
  indusind_bank: number;
  au_bank: number;
  ex_showroom: number;
  tcs: number;
  registration: number;
  insurance: number;
  number_plate_crtm_autocard: number;
  gps: number;
  fastag: number;
  speed_governor: number;
  accessories: number;
  on_the_road: number;
  loan_amount: number;
  margin_down_payment: number;
  process_fee: number;
  stamp_duty: number;
  handling_document_charge: number;
  loan_suraksha_insurance: number;
  down_payment: number;
  offers: number;
  final_down_payment: number;
  emi_years: number;
  monthly_emi: number;
  image_url?: string;
  image?: string;
  created_at: string;
  updated_at: string;
}

const QuotationCreationPage: React.FC = () => {
  const navigate = useNavigate();
  
  // State for modals and UI
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isQuotationModalOpen, setIsQuotationModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submittedQuotationId, setSubmittedQuotationId] = useState<string | null>(null);
  const [quotationData, setQuotationData] = useState<QuotationResponse | null>(null);
  const [backendStatus, setBackendStatus] = useState<boolean | null>(null);
  const [backendStatusChecked, setBackendStatusChecked] = useState<boolean>(false);
  
  // State for form data
  const dispatch = useAppDispatch();
  const [quotationCarType, setQuotationCarType] = useState<'t-permit' | 'private'>('t-permit');
  
  // Brand selection
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [isCustomBrand, setIsCustomBrand] = useState(false);
  const [customBrandName, setCustomBrandName] = useState('');

  // Model selection
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [isCustomModel, setIsCustomModel] = useState(false);
  const [customModelName, setCustomModelName] = useState('');

  // Variant selection
  const [selectedVariant, setSelectedVariant] = useState<string>('');
  const [isCustomVariant, setIsCustomVariant] = useState(false);
  const [customVariantName, setCustomVariantName] = useState('');

  // Car Image & Server Upload
  const [carImage, setCarImage] = useState<string>('');
  const [imagePreview, setImagePreview] = useState<string>('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const [inventoryStruct, setInventoryStruct] = useState<InventoryStructure>(getInventoryStructure());
  const [dynamicCarVariants, setDynamicCarVariants] = useState<Record<string, string[]>>(getDynamicCarVariants());
  const [errors, setErrors] = useState<FormErrors>({});
  const [currentStep, setCurrentStep] = useState<number>(1);

  useEffect(() => {
    fetchCarInventoryFromServer();
    const updateCars = () => {
      setDynamicCarVariants(getDynamicCarVariants());
      setInventoryStruct(getInventoryStructure());
    };
    window.addEventListener('asw_cars_updated', updateCars);
    return () => window.removeEventListener('asw_cars_updated', updateCars);
  }, []);
  
  const initialFormData: QuotationData = {
    emiInterestRate: '',
    showroomCost: '',
    tcs: '',
    registration: '',
    insurance: '',
    noPlate: '',
    gps: '',
    fastag: '',
    speedGovernor: '',
    accessories: '',
    onRoadPrice: '',
    loanAmount: '',
    margin: '',
    processFee: '',
    stampDuty: '',
    handlingCharge: '',
    loanInsurance: '',
    // Bank percentage fields
    sbiBank: '',
    unionBank: '',
    indusIndBank: '',
    auBank: '',
    // Additional financial fields
    downPayment: '',
    offers: '',
    finalDownPayment: '',
    emiYears: 'None',
    emiFor5Years: '',
    customerName: '',
    customerPhone: '',
    customerAddress: ''
  };

  // Form data state
  const [formData, setFormData] = useState<QuotationData>(initialFormData);

  // Load quotation data if we have a submitted ID
  useEffect(() => {
    if (submittedQuotationId) {
      fetchQuotationData(submittedQuotationId);
    }
  }, [submittedQuotationId]);

  // Fetch quotation data from the backend
  const fetchQuotationData = async (vendorId: string) => {
    try {
      const data = await quotationService.getQuotationByVendor(vendorId);
      setQuotationData(data as QuotationResponse);
    } catch (error) {
      console.error('Error fetching quotation data:', error);
    }
  };

  // Check backend server status
  const checkBackendStatus = async () => {
    try {
      const isRunning = await quotationService.checkServerStatus();
      setBackendStatus(isRunning);
      setBackendStatusChecked(true);
      return isRunning;
    } catch (error) {
      console.error('Error checking backend status:', error);
      setBackendStatus(false);
      setBackendStatusChecked(true);
      return false;
    }
  };

  // Check backend status on component mount
  useEffect(() => {
    checkBackendStatus();
  }, []);

  // Handlers for car selection (Brand -> Model -> Variant)
  const handleBrandSelect = (brand: string) => {
    setSelectedBrand(brand);
    setIsCustomBrand(false);
    setSelectedModel('');
    setIsCustomModel(false);
    setSelectedVariant('');
    setIsCustomVariant(false);
    setCarImage('');
    setImagePreview('');
    setFormData({ ...initialFormData });
    setErrors({});
  };

  const handleModelSelect = (model: string) => {
    setSelectedModel(model);
    setIsCustomModel(false);
    setSelectedVariant('');
    setIsCustomVariant(false);

    // Auto-detect image and category for this model
    const key = `${selectedBrand} ${model}`.trim();
    if (inventoryStruct.imagesByModel[key]) {
      setImagePreview(inventoryStruct.imagesByModel[key]);
    } else if (inventoryStruct.imagesByModel[model]) {
      setImagePreview(inventoryStruct.imagesByModel[model]);
    }

    if (inventoryStruct.typesByModel[key]) {
      setQuotationCarType(inventoryStruct.typesByModel[key]);
    } else if (inventoryStruct.typesByModel[model]) {
      setQuotationCarType(inventoryStruct.typesByModel[model]);
    }

    setFormData({ ...initialFormData });
    setErrors({});
  };

  const handleVariantSelect = (variant: string) => {
    setSelectedVariant(variant);
    setFormData({ ...initialFormData });
    setErrors({});
  };

  const handleCarImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please upload a valid image file (PNG, JPG, JPEG, WEBP)');
        return;
      }
      setIsUploadingImage(true);
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result as string;
        setImagePreview(base64);

        // Upload directly to server SSD storage
        try {
          const baseUrl = getBackendBaseUrl();
          const res = await fetch(`${baseUrl}/api/upload`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              image: base64,
              type: 'cars',
              filename: file.name
            })
          });
          if (res.ok) {
            const data = await res.json();
            if (data.success && data.url) {
              setCarImage(data.url);
              console.log('✅ Uploaded vehicle photo to VPS server:', data.url);
            } else {
              setCarImage(base64);
            }
          } else {
            setCarImage(base64);
          }
        } catch (uploadErr) {
          console.warn('Server upload fallback to base64:', uploadErr);
          setCarImage(base64);
        } finally {
          setIsUploadingImage(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setCarImage('');
    setImagePreview('');
  };

  // Modal handlers
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    if (!isModalOpen) {
      setSelectedBrand('');
      setIsCustomBrand(false);
      setCustomBrandName('');
      setSelectedModel('');
      setIsCustomModel(false);
      setCustomModelName('');
      setSelectedVariant('');
      setIsCustomVariant(false);
      setCustomVariantName('');
      setCarImage('');
      setImagePreview('');
      setQuotationCarType('t-permit');
      setErrors({});
    }
  };
  
  const openQuotationModal = () => {
    const brandToUse = isCustomBrand ? customBrandName.trim() : selectedBrand;
    const modelToUse = isCustomModel ? customModelName.trim() : selectedModel;
    const variantToUse = (isCustomVariant || isCustomModel || isCustomBrand) ? customVariantName.trim() : selectedVariant;

    if (!brandToUse) {
      setErrors({ modal: 'Please select or enter a Car Brand Name' });
      return;
    }
    if (!modelToUse) {
      setErrors({ modal: 'Please select or enter a Car Model Name' });
      return;
    }
    if (!variantToUse) {
      setErrors({ modal: 'Please select or enter a Model Variant' });
      return;
    }

    const fullCarName = modelToUse.toLowerCase().startsWith(brandToUse.toLowerCase())
      ? modelToUse
      : `${brandToUse} ${modelToUse}`.trim();

    setSelectedBrand(fullCarName);
    setSelectedVariant(variantToUse);
    setIsModalOpen(false);
    setIsQuotationModalOpen(true);
    setErrors({});
    setCurrentStep(1);
  };

  const closeQuotationModal = () => {
    setIsQuotationModalOpen(false);
    setCurrentStep(1);
  };

  // Pure manual form input handler without auto-calculations
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Handle bank percentages (only one bank can be active at a time)
    if (name === 'sbiBank' || name === 'unionBank' || name === 'indusIndBank' || name === 'auBank') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        sbiBank: name === 'sbiBank' ? value : '',
        unionBank: name === 'unionBank' ? value : '',
        indusIndBank: name === 'indusIndBank' ? value : '',
        auBank: name === 'auBank' ? value : ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle form submission with pure manual input values
  const handleQuotationSubmit = async () => {
    setIsSubmitting(true);
    setErrors({});

    try {
      if (!backendStatusChecked) {
        const serverRunning = await checkBackendStatus();
        if (!serverRunning) {
          setErrors({ submit: "Cannot connect to the backend server. Please make sure the server is running." });
          setIsSubmitting(false);
          return;
        }
      }

      const quotationData = {
        vendor_id: `V-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
        car_model: selectedBrand,
        model_variant: selectedVariant,
        car_type: quotationCarType,
        image_url: carImage || imagePreview || null,
        roi_emi_interest: parseFloat(formData.emiInterestRate) || 0,
        sbi_bank: parseFloat(formData.sbiBank) || 0,
        union_bank: parseFloat(formData.unionBank) || 0,
        indusind_bank: parseFloat(formData.indusIndBank) || 0,
        au_bank: parseFloat(formData.auBank) || 0,
        ex_showroom: parseFloat(formData.showroomCost) || 0,
        tcs: parseFloat(formData.tcs) || 0,
        registration: parseFloat(formData.registration) || 0,
        insurance: parseFloat(formData.insurance) || 0,
        number_plate_crtm_autocard: parseFloat(formData.noPlate) || 0,
        gps: parseFloat(formData.gps) || 0,
        fastag: parseFloat(formData.fastag) || 0,
        speed_governor: parseFloat(formData.speedGovernor) || 0,
        accessories: parseFloat(formData.accessories) || 0,
        on_the_road: parseFloat(formData.onRoadPrice) || 0,
        loan_amount: parseFloat(formData.loanAmount) || 0,
        margin_down_payment: parseFloat(formData.margin) || 0,
        process_fee: parseFloat(formData.processFee) || 0,
        stamp_duty: parseFloat(formData.stampDuty) || 0,
        handling_document_charge: parseFloat(formData.handlingCharge) || 0,
        loan_suraksha_insurance: parseFloat(formData.loanInsurance) || 0,
        down_payment: parseFloat(formData.downPayment) || 0,
        offers: parseFloat(formData.offers) || 0,
        final_down_payment: parseFloat(formData.finalDownPayment) || 0,
        emi_years: formData.emiYears === 'None' ? 0 : parseInt(formData.emiYears) || 0,
        monthly_emi: parseFloat(formData.emiFor5Years) || 0,
      };
      
      const response = await quotationService.createQuotation(quotationData) as QuotationResponse;

      // Update car inventory and broadcast update with clean server path
      const finalImageUrl = response?.image_url || response?.image || carImage || imagePreview || undefined;
      upsertCarToInventory(selectedBrand, selectedVariant, quotationCarType, finalImageUrl);

      // Force refresh Redux cars so homepage immediately shows the new quotation/car
      dispatch(globalForceRefreshCarsData());
      
      setQuotationData(response);
      setSubmitSuccess(true);
      setSubmittedQuotationId(response.vendor_id);
      setIsSubmitting(false);
      
      setTimeout(() => {
        setIsQuotationModalOpen(false);
        navigate('/dashboard/quotations', { 
          state: { 
            success: true, 
            message: `Quotation ${response.vendor_id} generated successfully`,
            quotationData: response
          }
        });
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting quotation:', error);
      setIsSubmitting(false);
      
      let errorMessage = "Failed to submit quotation. Please try again.";
      if (error instanceof Error) {
        if (error.message.includes('Validation failed')) {
          errorMessage = error.message.replace('Validation failed: ', '');
        } else if (error.message.includes('Network error')) {
          errorMessage = "Cannot connect to server. Please check your connection.";
        } else if (error.message) {
          errorMessage = error.message;
        }
      }
      
      setErrors({ submit: errorMessage });
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <div className="step-header">
              <h3>Step 1: Vehicle & Financing Information</h3>
              <div className="step-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: '33%' }}></div>
                </div>
                <span>1 of 3</span>
              </div>
            </div>
            
            <div className="form-grid">
              <div className="form-section">
                <h4>Vehicle Information</h4>
                <div className="form-row highlight-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '0.4rem 0.6rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {imagePreview && (
                      <img 
                        src={imagePreview} 
                        alt={selectedBrand} 
                        style={{ width: '44px', height: '32px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #cbd5e1' }} 
                      />
                    )}
                    <div>
                      <span style={{ 
                        display: 'inline-block', 
                        marginRight: '6px', 
                        fontSize: '0.72rem', 
                        padding: '1px 6px', 
                        borderRadius: '4px', 
                        background: quotationCarType === 't-permit' ? '#fef3c7' : '#e0e7ff', 
                        color: quotationCarType === 't-permit' ? '#b45309' : '#3730a3', 
                        fontWeight: 600 
                      }}>
                        {quotationCarType === 't-permit' ? 'T-Permit' : 'Private'}
                      </span>
                      <strong style={{ fontSize: '0.9rem' }}>{`${selectedBrand} ${selectedVariant}`}</strong>
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <label>ROI / EMI Interest (%):</label>
                  <input 
                    type="number" 
                    name="emiInterestRate"
                    value={formData.emiInterestRate}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Enter interest rate"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="form-section">
                <h4>Bank Interest Rates (%) - Select One</h4>
                <div className="bank-info-text">
                  <small>Each bank has different loan calculation methods. Select one bank and enter the loan percentage.</small>
                </div>
                <div className="bank-grid">
                  <div className="bank-field sbi">
                    <label>SBI Bank:</label>
                    <input 
                      type="number" 
                      name="sbiBank"
                      value={formData.sbiBank}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Rate"
                      step="0.01"
                    />
                    <small className="bank-help">Loan on: Ex-Showroom + TCS + Insurance + Registration</small>
                  </div>
                  <div className="bank-field union">
                    <label>Union Bank:</label>
                    <input 
                      type="number" 
                      name="unionBank"
                      value={formData.unionBank}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Rate"
                      step="0.01"
                    />
                    <small className="bank-help">Loan on: Full On Road Price (all components)</small>
                  </div>
                  <div className="bank-field indusind">
                    <label>IndusInd Bank:</label>
                    <input 
                      type="number" 
                      name="indusIndBank"
                      value={formData.indusIndBank}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Rate"
                      step="0.01"
                    />
                    <small className="bank-help">Loan on: Ex-Showroom price only</small>
                  </div>
                  <div className="bank-field au">
                    <label>AU Bank:</label>
                    <input 
                      type="number" 
                      name="auBank"
                      value={formData.auBank}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Rate"
                      step="0.01"
                    />
                    <small className="bank-help">Loan on: Ex-Showroom price only</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="step-content">
            <div className="step-header">
              <h3>Step 2: Cost Breakdown</h3>
              <div className="step-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: '66%' }}></div>
                </div>
                <span>2 of 3</span>
              </div>
            </div>
            
            <div className="form-grid">
              <div className="form-section">
                <h4>Base Vehicle Costs</h4>
                <div className="cost-row">
                  <label>Ex-Showroom Price: <span className="required">*</span></label>
                  <input 
                    type="number" 
                    name="showroomCost"
                    value={formData.showroomCost}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Enter ex-showroom price"
                    required
                  />
                </div>
                <div className="cost-row">
                  <label>TCS:</label>
                  <input 
                    type="number" 
                    name="tcs"
                    value={formData.tcs}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="TCS amount"
                  />
                </div>
                <div className="cost-row">
                  <label>Registration: <span className="required">*</span></label>
                  <input 
                    type="number" 
                    name="registration"
                    value={formData.registration}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Registration fee"
                    required
                  />
                </div>
                <div className="cost-row">
                  <label>Insurance: <span className="required">*</span></label>
                  <input 
                    type="number" 
                    name="insurance"
                    value={formData.insurance}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Insurance amount"
                    required
                  />
                </div>
              </div>
              
              <div className="form-section">
                <h4>Additional Components</h4>
                <div className="cost-row">
                  <label>Number Plate + CRTM + Autocard:</label>
                  <input 
                    type="number" 
                    name="noPlate"
                    value={formData.noPlate}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Amount"
                  />
                </div>
                <div className="cost-row">
                  <label>GPS:</label>
                  <input 
                    type="number" 
                    name="gps"
                    value={formData.gps}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="GPS cost"
                  />
                </div>
                <div className="cost-row">
                  <label>Fastag:</label>
                  <input 
                    type="number" 
                    name="fastag"
                    value={formData.fastag}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Fastag cost"
                  />
                </div>
                <div className="cost-row">
                  <label>Speed Governor:</label>
                  <input 
                    type="number" 
                    name="speedGovernor"
                    value={formData.speedGovernor}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Speed governor cost"
                  />
                </div>
                <div className="cost-row">
                  <label>Accessories:</label>
                  <input 
                    type="number" 
                    name="accessories"
                    value={formData.accessories}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Accessories cost"
                  />
                </div>
              </div>
            </div>
            
            <div className="total-section">
              <div className="cost-row highlight-row">
                <label>On the Road Price: <span className="required">*</span></label>
                <input 
                  type="number" 
                  name="onRoadPrice"
                  value={formData.onRoadPrice}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="Enter on-road price"
                  required
                />
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="step-content">
            <div className="step-header">
              <h3>Step 3: Loan & Payment Details</h3>
              <div className="step-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: '100%' }}></div>
                </div>
                <span>3 of 3</span>
              </div>
            </div>
            
            <div className="form-grid">
              <div className="form-section">
                <h4>Loan Information</h4>
                <div className="cost-row">
                  <label>Loan Amount:</label>
                  <input 
                    type="number" 
                    name="loanAmount"
                    value={formData.loanAmount}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Enter loan amount"
                  />
                </div>
                <div className="cost-row">
                  <label>Margin (Down Payment):</label>
                  <input 
                    type="number" 
                    name="margin"
                    value={formData.margin}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Enter margin"
                  />
                </div>
                <div className="cost-row">
                  <label>Process Fee:</label>
                  <input 
                    type="number" 
                    name="processFee"
                    value={formData.processFee}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Process fee"
                  />
                </div>
                <div className="cost-row">
                  <label>Stamp Duty:</label>
                  <input 
                    type="number" 
                    name="stampDuty"
                    value={formData.stampDuty}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Stamp duty"
                  />
                </div>
              </div>
              
              <div className="form-section">
                <h4>Payment Details</h4>
                <div className="cost-row">
                  <label>Handling & Document Charge:</label>
                  <input 
                    type="number" 
                    name="handlingCharge"
                    value={formData.handlingCharge}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Handling charge"
                  />
                </div>
                <div className="cost-row">
                  <label>Loan Insurance:</label>
                  <input 
                    type="number" 
                    name="loanInsurance"
                    value={formData.loanInsurance}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Loan insurance"
                  />
                </div>
                <div className="cost-row">
                  <label>Total Down Payment:</label>
                  <input 
                    type="number" 
                    name="downPayment"
                    value={formData.downPayment}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Enter down payment"
                  />
                </div>
                <div className="cost-row">
                  <label>Offers/Discounts:</label>
                  <input 
                    type="number" 
                    name="offers"
                    value={formData.offers}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Discount/offers"
                  />
                </div>
                <div className="cost-row highlight-row">
                  <label>Final Down Payment:</label>
                  <input 
                    type="number" 
                    name="finalDownPayment"
                    value={formData.finalDownPayment}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Enter final down payment"
                  />
                </div>
              </div>
            </div>

            <div className="emi-section">
              <h4>EMI Details</h4>
              <div className="form-grid">
                <div className="cost-row">
                  <label>EMI Period:</label>
                  <select
                    name="emiYears"
                    value={formData.emiYears}
                    onChange={handleInputChange}
                    className="form-control"
                  >
                    <option value="None">None</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(year => (
                      <option key={year} value={year}>
                        {year} {year === 1 ? 'Year' : 'Years'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="cost-row">
                  <label>Monthly EMI:</label>
                  <input 
                    type="number" 
                    name="emiFor5Years"
                    value={formData.emiFor5Years}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Enter monthly EMI"
                  />
                  {formData.emiYears !== 'None' && formData.emiFor5Years && (
                    <small className="help-text">
                      ₹{parseFloat(formData.emiFor5Years).toLocaleString('en-IN')}/month for {formData.emiYears} {parseInt(formData.emiYears) === 1 ? 'Year' : 'Years'}
                    </small>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-welcome">
        <div className="welcome-header-row">
          <div>
            <h1>Create Quotation</h1>
            <p>Generate vehicle quotations with T-Permit commercial or private individual financing.</p>
          </div>
          <button 
            className="quotation-primary-btn"
            onClick={toggleModal}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span>Start New Quotation</span>
          </button>
        </div>
      </div>
      
      <div className="quotation-creator-wrapper">
        <div className="quotation-card">
          <div className="quotation-card-top">
            <span className="card-icon blue-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="12" y1="18" x2="12" y2="12"></line>
                <line x1="9" y1="15" x2="15" y2="15"></line>
              </svg>
            </span>
            <div>
              <h3>Vehicle Quotation Builder</h3>
              <p>Configure on-road price components, bank loan calculations, down payment, and monthly EMI.</p>
            </div>
          </div>
          <button 
            className="quotation-btn" 
            onClick={toggleModal}
          >
            Launch Quotation Wizard →
          </button>
        </div>
        
        <div className="quotation-help-card">
          <div className="help-card-header">
            <span className="card-icon emerald-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </span>
            <h3>Quick Workflow Guide</h3>
          </div>
          <ul className="quotation-help-list">
            <li>
              <span className="bullet-check">✓</span>
              <span><strong>Step 0:</strong> Select T-Permit or Private category, model & upload photo</span>
            </li>
            <li>
              <span className="bullet-check">✓</span>
              <span><strong>Step 1:</strong> Select bank financing (SBI, Union, IndusInd, AU) and interest rate</span>
            </li>
            <li>
              <span className="bullet-check">✓</span>
              <span><strong>Step 2:</strong> Input ex-showroom price, RTO registration, insurance & charges</span>
            </li>
            <li>
              <span className="bullet-check">✓</span>
              <span><strong>Step 3:</strong> Finalize loan amount, down payment, offers, and EMI tenure</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Car Selection Modal */}
      {isModalOpen && (
        <div className="quotation-modal-overlay" onClick={(e) => e.target === e.currentTarget && toggleModal()}>
          <div className="quotation-modal car-selection-modal" onClick={(e) => e.stopPropagation()}>
            <div className="quotation-modal-header">
              <div>
                <h2>Create New Quotation</h2>
                <span className="modal-subtitle">Step 0: Choose Classification & Vehicle Details</span>
              </div>
              <button className="close-modal-btn" onClick={toggleModal}>×</button>
            </div>
            
            <div className="quotation-modal-body">
              {errors.modal && (
                <div className="modal-error-banner" style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 12px', borderRadius: '6px', marginBottom: '12px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>{errors.modal}</span>
                </div>
              )}

              {/* 1. Category Classification */}
              <div className="form-section category-selection-section">
                <label className="section-title-label">1. Select Quotation Category</label>
                <div className="quotation-category-toggle-grid">
                  <button
                    type="button"
                    className={`category-card-btn ${quotationCarType === 't-permit' ? 'active tpermit' : ''}`}
                    onClick={() => setQuotationCarType('t-permit')}
                  >
                    <div className="cat-icon">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="1" y="3" width="15" height="13"></rect>
                        <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                        <circle cx="5.5" cy="18.5" r="2.5"></circle>
                        <circle cx="18.5" cy="18.5" r="2.5"></circle>
                      </svg>
                    </div>
                    <div className="cat-details">
                      <span className="cat-title">T-Permit (Commercial)</span>
                      <span className="cat-desc">Commercial Tourist Permit with 100% On-Road Funding</span>
                    </div>
                    {quotationCarType === 't-permit' && <span className="cat-check">✓ Active</span>}
                  </button>

                  <button
                    type="button"
                    className={`category-card-btn ${quotationCarType === 'private' ? 'active private' : ''}`}
                    onClick={() => setQuotationCarType('private')}
                  >
                    <div className="cat-icon">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.5 2.8C2.1 11 2 11.5 2 12v4c0 .6.4 1 1 1h2"></path>
                        <circle cx="7" cy="17" r="2"></circle>
                        <path d="M9 17h6"></path>
                        <circle cx="17" cy="17" r="2"></circle>
                      </svg>
                    </div>
                    <div className="cat-details">
                      <span className="cat-title">Private (Personal)</span>
                      <span className="cat-desc">Individual Personal Registration & Lowest Down Payment</span>
                    </div>
                    {quotationCarType === 'private' && <span className="cat-check">✓ Active</span>}
                  </button>
                </div>
              </div>

              {/* 2. Car Brand Name */}
              <div className="form-section brand-selection-section">
                <div className="section-label-row">
                  <label className="section-title-label">2. Car Brand Name <span className="required">*</span></label>
                  <button 
                    type="button" 
                    className="toggle-mode-btn"
                    onClick={() => {
                      setIsCustomBrand(!isCustomBrand);
                      if (!isCustomBrand) {
                        setSelectedBrand('');
                        setIsCustomModel(true);
                        setIsCustomVariant(true);
                      } else {
                        setCustomBrandName('');
                      }
                    }}
                  >
                    {isCustomBrand ? '← Choose Existing Brand' : '+ Add New Brand'}
                  </button>
                </div>

                {isCustomBrand ? (
                  <div className="input-with-action">
                    <input 
                      type="text" 
                      className="form-control"
                      placeholder="Enter brand name (e.g. Maruti Suzuki, Tata, Hyundai, Toyota, Mahindra)"
                      value={customBrandName}
                      onChange={(e) => setCustomBrandName(e.target.value)}
                      autoFocus
                    />
                  </div>
                ) : (
                  <div className="custom-dropdown">
                    <select 
                      value={selectedBrand} 
                      onChange={(e) => handleBrandSelect(e.target.value)}
                      className="form-control"
                    >
                      <option value="">-- Select Car Brand --</option>
                      {inventoryStruct.brands.map(brand => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* 3. Car Model Name */}
              {(selectedBrand || isCustomBrand) && (
                <div className="form-section model-selection-section">
                  <div className="section-label-row">
                    <label className="section-title-label">3. Car Model Name <span className="required">*</span></label>
                    {!isCustomBrand && (
                      <button 
                        type="button" 
                        className="toggle-mode-btn"
                        onClick={() => {
                          setIsCustomModel(!isCustomModel);
                          if (!isCustomModel) {
                            setSelectedModel('');
                            setIsCustomVariant(true);
                          } else {
                            setCustomModelName('');
                          }
                        }}
                      >
                        {isCustomModel ? '← Choose Existing Model' : '+ Add New Model'}
                      </button>
                    )}
                  </div>

                  {isCustomModel || isCustomBrand ? (
                    <div className="input-with-action">
                      <input 
                        type="text" 
                        className="form-control"
                        placeholder="Enter car model (e.g. Ertiga, Wagon-R, Nexon, Punch, Aura, Dzire)"
                        value={customModelName}
                        onChange={(e) => setCustomModelName(e.target.value)}
                        autoFocus={isCustomModel}
                      />
                    </div>
                  ) : (
                    <div className="custom-dropdown">
                      <select 
                        value={selectedModel} 
                        onChange={(e) => handleModelSelect(e.target.value)}
                        className="form-control"
                      >
                        <option value="">-- Select Car Model --</option>
                        {(inventoryStruct.modelsByBrand[selectedBrand] || []).map(model => (
                          <option key={model} value={model}>{model}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* 4. Model Variant */}
              {(selectedModel || isCustomModel || isCustomBrand) && (
                <div className="form-section variant-selection-section">
                  <div className="section-label-row">
                    <label className="section-title-label">4. Model Variant <span className="required">*</span></label>
                    {(!isCustomBrand && !isCustomModel) && (
                      <button 
                        type="button" 
                        className="toggle-mode-btn"
                        onClick={() => {
                          setIsCustomVariant(!isCustomVariant);
                          if (!isCustomVariant) {
                            setSelectedVariant('');
                          } else {
                            setCustomVariantName('');
                          }
                        }}
                      >
                        {isCustomVariant ? '← Choose Existing Variant' : '+ Add New Variant'}
                      </button>
                    )}
                  </div>

                  {isCustomVariant || isCustomModel || isCustomBrand ? (
                    <div className="input-with-action">
                      <input 
                        type="text" 
                        className="form-control"
                        placeholder="Enter model variant (e.g. Tour M CNG, Creative MT, Pure, VXI)"
                        value={customVariantName}
                        onChange={(e) => setCustomVariantName(e.target.value)}
                        autoFocus={isCustomVariant}
                      />
                    </div>
                  ) : (
                    <div className="custom-dropdown">
                      <select 
                        value={selectedVariant}
                        onChange={(e) => handleVariantSelect(e.target.value)}
                        className="form-control"
                      >
                        <option value="">-- Select Variant --</option>
                        {((inventoryStruct.variantsByModel[`${selectedBrand} ${selectedModel}`.trim()] || inventoryStruct.variantsByModel[selectedModel]) || []).map(variant => (
                          <option key={variant} value={variant}>{variant}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* 5. Car Image Upload */}
              <div className="form-section image-upload-section">
                <label className="section-title-label">5. Car Image / Photo (Uploads to Server & Displays on Homepage)</label>
                <div className="upload-box-container">
                  <input 
                    type="file" 
                    id="quotation-car-image-input" 
                    accept="image/*" 
                    style={{ display: 'none' }}
                    onChange={handleCarImageUpload}
                  />
                  {imagePreview ? (
                    <div className="image-preview-card">
                      <img src={imagePreview} alt="Car preview" className="preview-thumbnail" />
                      <div className="preview-meta">
                        <span className="preview-title">Photo Attached</span>
                        <span className="preview-tag">{carImage ? 'Custom uploaded' : 'Inventory default'}</span>
                      </div>
                      <div className="preview-actions">
                        <label htmlFor="quotation-car-image-input" className="change-img-action">Change</label>
                        <button type="button" className="remove-img-action" onClick={handleRemoveImage}>Remove</button>
                      </div>
                    </div>
                  ) : (
                    <label htmlFor="quotation-car-image-input" className="dropzone-upload-label">
                      <div className="dropzone-content">
                        <span className="dropzone-camera-icon" style={{ display: 'flex', alignItems: 'center', color: '#2563eb' }}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                            <circle cx="12" cy="13" r="4"></circle>
                          </svg>
                        </span>
                        <div className="dropzone-text-group">
                          <strong>Click to Upload Vehicle Photo</strong>
                          <span>Supports PNG, JPG, WEBP — Will automatically appear on Homepage</span>
                        </div>
                      </div>
                    </label>
                  )}
                </div>
              </div>
            </div>
            
            <div className="quotation-modal-footer">
              <button className="cancel-btn" onClick={toggleModal}>Cancel</button>
              <button 
                className="submit-btn" 
                onClick={openQuotationModal}
                disabled={
                  isCustomBrand 
                    ? !customBrandName.trim() || !customVariantName.trim() 
                    : !selectedBrand || (isCustomVariant ? !customVariantName.trim() : !selectedVariant)
                }
              >
                Continue to Financing & Costs →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Quotation Details Modal */}
      {isQuotationModalOpen && (
        <div className="quotation-modal-overlay" onClick={(e) => e.target === e.currentTarget && closeQuotationModal()}>
          <div className="quotation-modal quotation-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="quotation-modal-header">
              <h2>Quotation Details - {selectedBrand} {selectedVariant}</h2>
              <button className="close-modal-btn" onClick={closeQuotationModal}>×</button>
            </div>
            
            {/* Error Display */}
            {errors.submit && (
              <div className="error-banner">
                <div className="error-icon" style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                </div>
                <div className="error-text">
                  <strong>Error:</strong> {errors.submit}
                </div>
                <button 
                  className="error-close-btn"
                  onClick={() => setErrors({ ...errors, submit: '' })}
                >
                  ×
                </button>
              </div>
            )}
            
            <div className="quotation-modal-body">
              {renderStepContent()}
            </div>
            
            <div className="quotation-modal-footer">
              <div className="footer-left">
                {currentStep > 1 && (
                  <button 
                    className="prev-btn"
                    onClick={() => setCurrentStep(currentStep - 1)}
                  >
                    ← Previous
                  </button>
                )}
              </div>
              
              <div className="footer-right">
                <button className="cancel-btn" onClick={closeQuotationModal}>
                  Cancel
                </button>
                
                {currentStep < 3 ? (
                  <button 
                    className="next-btn"
                    onClick={() => setCurrentStep(currentStep + 1)}
                  >
                    Next →
                  </button>
                ) : (
                  <button 
                    className={`submit-btn ${isSubmitting ? 'loading' : ''} ${submitSuccess ? 'success' : ''}`}
                    onClick={handleQuotationSubmit}
                    disabled={isSubmitting || submitSuccess}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-icon"></span>
                        Processing...
                      </>
                    ) : submitSuccess ? (
                      <>
                        <span className="success-icon">✓</span>
                        Quotation Generated!
                      </>
                    ) : (
                      'Generate Quotation'
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {submitSuccess && quotationData && (
        <div className="quotation-success-overlay">
          <div className="quotation-success-modal">
            <div className="quotation-modal-header">
              <h2>Quotation Generated Successfully</h2>
              <button 
                className="close-modal-btn"
                onClick={() => navigate('/dashboard/quotations')}
              >×</button>
            </div>
            
            <div className="quotation-modal-body">
              <div className="quotation-success-message">
                <div className="success-icon-large">✓</div>
                <h3>Quotation #{quotationData.vendor_id}</h3>
                <p className="quotation-date">Created on: {new Date(quotationData.created_at).toLocaleDateString('en-IN')}</p>
              </div>
              
              <div className="quotation-details-summary">
                <div className="quotation-detail-item">
                  <span>Car Model:</span>
                  <strong>{quotationData.car_model} {quotationData.model_variant}</strong>
                </div>
                <div className="quotation-detail-item">
                  <span>On Road Price:</span>
                  <strong>₹{quotationData.on_the_road.toLocaleString('en-IN')}</strong>
                </div>
                <div className="quotation-detail-item">
                  <span>Down Payment:</span>
                  <strong>₹{quotationData.final_down_payment.toLocaleString('en-IN')}</strong>
                </div>
                {quotationData.monthly_emi > 0 && (
                  <div className="quotation-detail-item">
                    <span>Monthly EMI:</span>
                    <strong>₹{quotationData.monthly_emi.toLocaleString('en-IN')}/month</strong>
                    {quotationData.emi_years > 0 && <small>for {quotationData.emi_years} years</small>}
                  </div>
                )}
              </div>
              
              <div className="quotation-actions">
                <button 
                  className="view-quotation-btn"
                  onClick={() => navigate(`/dashboard/quotations/${quotationData.vendor_id}`)}
                >
                  View Quotation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationCreationPage;